import {
  docFrontmatterSchema,
  approvedLegalProblem,
  legalFrontmatterSchema,
  legalSlugs,
  SLUG_PATTERN,
  type DocFrontmatter,
  type LegalFrontmatter,
  type LegalSlug,
} from '@shared/content-schemas';
import { defaultLocale, resolveLocale, type Locale } from '@shared/locales';
import { groupByLocale } from './localized-files';
import { parseFrontmatter, renderMarkdown } from './markdown';
import { expandGeneratedContent } from './permissions-doc';

/**
 * Documentation and legal pages, loaded from content/docs and content/legal
 * at build time. Frontmatter is validated on import, so a broken CMS edit
 * fails the tests and the build. Generated tables (from
 * contract/permissions.json) are spliced in here, before anything renders, so
 * the HTML, the Markdown twins and llms-full.txt all carry them.
 */

export interface DocArticle extends DocFrontmatter {
  slug: string;
  locale: Locale;
  /** Body with generated tables expanded; comments still present. */
  markdown: string;
  html: string;
}

export interface LegalPage extends LegalFrontmatter {
  slug: LegalSlug;
  locale: Locale;
  markdown: string;
  html: string;
}

/**
 * The slug is the URL. Anything else would have no route, no Markdown twin and
 * a broken llms.txt link, so loading the content (and therefore the build)
 * stops here.
 */
export function assertUrlSlug(slug: string, file: string): void {
  if (!SLUG_PATTERN.test(slug)) {
    throw new Error(
      `${file}: the file name must be lowercase ASCII letters, digits and dashes (e.g. getting-started.md)`,
    );
  }
}

function buildDoc(slug: string, raw: string, locale: Locale): DocArticle {
  assertUrlSlug(slug, `content/docs/${slug}${locale === defaultLocale ? '' : `.${locale}`}.md`);
  const { data, body } = parseFrontmatter(raw);
  const parsed = docFrontmatterSchema.safeParse(data);
  if (!parsed.success) {
    throw new Error(`content/docs/${slug}${locale === defaultLocale ? '' : `.${locale}`}.md: ${parsed.error.message}`);
  }
  const markdown = expandGeneratedContent(body, locale);
  let rendered: string | undefined;
  return {
    ...parsed.data,
    slug,
    locale,
    markdown,
    get html() {
      rendered ??= renderMarkdown(markdown, locale);
      return rendered;
    },
  };
}

/** Fields that decide a legal page's standing; every translation must agree with the English page. */
const SHARED_LEGAL_FIELDS = ['status', 'effectiveDate', 'updated'] as const;

/**
 * Stops the build when a legal page's translations disagree on its status,
 * effective date or last update — e.g. English approved and indexed while
 * Spanish is still a draft, which would give one policy two legal standings
 * and contradictory search metadata (hreflang between an indexed page and a
 * noindex one). The CMS shares these fields between languages; this catches
 * hand edits.
 */
export function assertLegalPairsMatch(
  byLocale: Map<string, Map<string, Pick<LegalFrontmatter, (typeof SHARED_LEGAL_FIELDS)[number]>>>,
): void {
  const english = byLocale.get(defaultLocale) ?? new Map();
  const problems: string[] = [];
  for (const [locale, pages] of byLocale) {
    if (locale === defaultLocale) continue;
    for (const [slug, page] of pages) {
      const original = english.get(slug);
      if (!original) continue;
      for (const field of SHARED_LEGAL_FIELDS) {
        if (page[field] !== original[field]) {
          problems.push(
            `content/legal/${slug}.${locale}.md: ${field} is "${page[field]}" but the English page has "${original[field]}"`,
          );
        }
      }
    }
  }
  if (problems.length)
    throw new Error(`Legal translations must share status, effectiveDate and updated:\n${problems.join('\n')}`);
}

function buildLegal(slug: string, raw: string, locale: Locale): LegalPage {
  const file = `content/legal/${slug}${locale === defaultLocale ? '' : `.${locale}`}.md`;
  if (!(legalSlugs as readonly string[]).includes(slug)) throw new Error(`${file}: unknown legal page "${slug}"`);
  const { data, body } = parseFrontmatter(raw);
  const parsed = legalFrontmatterSchema.safeParse(data);
  if (!parsed.success) throw new Error(`${file}: ${parsed.error.message}`);
  // The raw frontmatter, not the parsed one: every field is checked, including any the schema does not know.
  const problem = approvedLegalProblem(data, body);
  if (problem) throw new Error(`${file} ${problem}`);
  let rendered: string | undefined;
  return {
    ...parsed.data,
    slug: slug as LegalSlug,
    locale,
    markdown: body,
    get html() {
      rendered ??= renderMarkdown(body, locale);
      return rendered;
    },
  };
}

const docFiles = import.meta.glob('../../../content/docs/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;
const legalFiles = import.meta.glob('../../../content/legal/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

const docsByLocale = groupByLocale(docFiles, buildDoc);
const legalByLocale = groupByLocale(legalFiles, buildLegal);
assertLegalPairsMatch(legalByLocale);

/**
 * Index order: by `order`, then title, then slug. Titles are compared by code
 * point, not with localeCompare: the order is computed once at build time and
 * again in the visitor's browser, and a locale-dependent comparison could sort
 * two equal-order articles differently there (a hydration mismatch). The slug
 * (unique) makes the order total.
 */
export function compareDocs(
  a: Pick<DocArticle, 'order' | 'title' | 'slug'>,
  b: Pick<DocArticle, 'order' | 'title' | 'slug'>,
): number {
  const byCodePoint = (x: string, y: string) => (x < y ? -1 : x > y ? 1 : 0);
  return a.order - b.order || byCodePoint(a.title, b.title) || byCodePoint(a.slug, b.slug);
}

/** English articles in index order (compareDocs). Translations follow the same order. */
const englishDocs = [...(docsByLocale.get(defaultLocale)?.values() ?? [])].sort(compareDocs);

export function getDocs(lang: string): DocArticle[] {
  const locale = resolveLocale(lang);
  const translations = docsByLocale.get(locale);
  return englishDocs.map((doc) => translations?.get(doc.slug) ?? doc);
}

export function getDoc(slug: string, lang: string): DocArticle | undefined {
  return getDocs(lang).find((doc) => doc.slug === slug);
}

export const docSlugs = (): string[] => englishDocs.map((doc) => doc.slug);

export function getLegalPage(slug: LegalSlug, lang: string): LegalPage | undefined {
  const locale = resolveLocale(lang);
  return legalByLocale.get(locale)?.get(slug) ?? legalByLocale.get(defaultLocale)?.get(slug);
}

/** Every loaded file, per locale — for the completeness tests. */
export function loadedContent() {
  return {
    docs: Object.fromEntries([...docsByLocale].map(([locale, docs]) => [locale, [...docs.keys()].sort()])),
    legal: Object.fromEntries([...legalByLocale].map(([locale, pages]) => [locale, [...pages.keys()].sort()])),
  };
}
